import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Button from '../UI/Buttons/Button';
import ModalDialog from '../UI/Dialog/ModalDialog';

const CODE_LENGTH = 6;

/**
 * Authenticator apps offered on the first setup step. Any RFC 6238 app works -- these are just
 * the ones most people already recognise, so nobody has to go hunting through a store.
 * Store links verified against the live App Store / Play Store listings.
 */
const AUTHENTICATOR_APPS = [
  {
    name: 'Google Authenticator',
    ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
    android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
  },
  {
    name: 'Microsoft Authenticator',
    ios: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
    android: 'https://play.google.com/store/apps/details?id=com.azure.authenticator'
  },
  {
    name: 'Twilio Authy',
    ios: 'https://apps.apple.com/app/twilio-authy/id494168017',
    android: 'https://play.google.com/store/apps/details?id=com.authy.authy'
  },
  {
    name: '1Password',
    ios: 'https://apps.apple.com/app/1password-password-manager/id1511601750',
    android: 'https://play.google.com/store/apps/details?id=com.onepassword.android'
  }
];

/**
 * The second step of an InternalDB sign-in.
 *
 * Driven by the `mfaTicket` handed back by /users/login once the password was accepted. Two entry
 * modes:
 *   mode="setup"  -- the user is not enrolled yet and 2FA is mandatory, so show the QR code,
 *                    confirm a code, then display the recovery codes exactly once.
 *   mode="verify" -- the user is enrolled; take a TOTP code or a recovery code.
 *
 * Either way the component ends by calling onAuthenticated with the login payload, which the
 * caller passes to the usual loginUser action. Local state only: the ticket and the codes must
 * not outlive the dialog.
 */
export default function TwoFactorDialog(props) {
  const { open, mode, ticket, userid, onCancel, onAuthenticated } = props;
  const { t } = useTranslation('translation');

  // 'apps' | 'loading' | 'scan' | 'code' | 'recovery'
  const [phase, setPhase] = useState(mode === 'setup' ? 'apps' : 'code');
  const [setupData, setSetupData] = useState(null);
  const [setupFailed, setSetupFailed] = useState(false);
  const [code, setCode] = useState('');
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const isSetup = mode === 'setup';

  const resetState = useCallback(() => {
    setPhase(isSetup ? 'apps' : 'code');
    setSetupData(null);
    setSetupFailed(false);
    setCode('');
    setUsingRecoveryCode(false);
    setRecoveryCodes(null);
    setPendingLogin(null);
    setError(null);
    setNotice(null);
    setBusy(false);
  }, [isSetup]);

  // Fetch the QR code as soon as a setup dialog opens, while the user is still reading the
  // app-download step. By the time they press Next it is almost always already there, so the
  // wizard gains a step without gaining a wait.
  useEffect(() => {
    let cancelled = false;
    if (!open || !isSetup || ticket == null) {
      return undefined;
    }
    setError(null);
    axios.post('/users/mfa/setup', { mfaTicket: ticket })
      .then(res => {
        if (cancelled) return;
        setSetupData(res.data);
      })
      .catch(err => {
        if (cancelled) return;
        setError(messageFrom(err, t('loginSupport')));
        setSetupFailed(true);
      });
    return () => { cancelled = true; };
  }, [open, isSetup, ticket, t]);

  // Release the spinner once the prefetch lands, for the case where the user got to Next first.
  useEffect(() => {
    if (phase === 'loading' && (setupData != null || setupFailed)) {
      setPhase('scan');
    }
  }, [phase, setupData, setupFailed]);

  // The dialog stays mounted for the life of the login screen, so `mode` is null on the render
  // that seeds `phase` -- a setup run would otherwise open on the scan step and only reach the
  // app list by pressing Back. Reset on open as well as on close, once `mode` is known.
  useEffect(() => {
    resetState();
  }, [open, resetState]);

  const codeValid = useMemo(() => {
    if (usingRecoveryCode) {
      return code.replace(/[^0-9a-zA-Z]/g, '').length >= 8;
    }
    return /^\d{6}$/.test(code.replace(/\s/g, ''));
  }, [code, usingRecoveryCode]);

  const submit = useCallback(async () => {
    if (!codeValid || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    const url = isSetup ? '/users/mfa/activate' : '/users/mfa/verify';
    try {
      const res = await axios.post(url, { mfaTicket: ticket, code: code.trim() });
      if (isSetup && res.data.recoveryCodes != null) {
        // Hold the session until the user confirms they have stored the codes -- dispatching the
        // login here would navigate away from the only screen that ever shows them.
        setRecoveryCodes(res.data.recoveryCodes);
        setPendingLogin(res.data);
        setPhase('recovery');
        setBusy(false);
        return;
      }
      onAuthenticated(res.data);
    } catch (err) {
      setError(messageFrom(err, t('loginSupport')));
      setCode('');
      setBusy(false);
    }
  }, [codeValid, busy, isSetup, ticket, code, onAuthenticated, t]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && codeValid && !busy) {
      e.preventDefault();
      submit();
    }
  };

  const handleCodeChange = (e) => {
    const raw = e.target.value;
    // TOTP codes are digits only; recovery codes are alphanumeric with a dash.
    setCode(usingRecoveryCode
      ? raw.toUpperCase().replace(/[^0-9A-Z-]/g, '').substring(0, 12)
      : raw.replace(/\D/g, '').substring(0, CODE_LENGTH));
  };

  const toggleRecoveryCode = () => {
    setUsingRecoveryCode(!usingRecoveryCode);
    setCode('');
    setError(null);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setNotice(t('copiedToClipboard'));
    } catch {
      // Clipboard access can be denied; the codes are on screen and downloadable regardless.
      setNotice(null);
    }
  };

  const downloadCodes = () => {
    const body = [t('recoveryCodes'), userid != null ? userid : '', '', ...recoveryCodes].join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'evora-recovery-codes.txt';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  // Leaving the app step is the only forward move that can outrun its data, so it is the only one
  // that ever shows a spinner.
  const goToScan = () => setPhase(setupData != null || setupFailed ? 'scan' : 'loading');

  const title = phase === 'apps'
    ? t('twoFactorChooseAppTitle')
    : (phase === 'recovery' ? t('recoveryCodes')
      : (isSetup ? t('twoFactorSetupTitle') : t('twoFactorAuthentication')));

  let buttons;
  if (phase === 'recovery') {
    buttons = (
      <Button variant="save" onClick={() => onAuthenticated(pendingLogin)}>
        {t('recoveryCodesSaved')}
      </Button>
    );
  } else if (phase === 'apps' || phase === 'loading') {
    buttons = (
      <>
        <Button variant="closeDialog" onClick={onCancel}>{t('cancel')}</Button>
        <Button variant="save" onClick={goToScan} disabled={phase === 'loading'}
                loading={phase === 'loading'}>
          {t('next')}
        </Button>
      </>
    );
  } else {
    buttons = (
      <>
        {/* On the scan step, Back returns to the app list rather than abandoning the sign-in --
            someone who realises mid-scan they have no app installed should not have to start
            over from the password. */}
        <Button variant="closeDialog" onClick={isSetup ? () => setPhase('apps') : onCancel}>
          {isSetup ? t('back') : t('cancel')}
        </Button>
        <Button variant="save" onClick={submit} disabled={!codeValid || busy} loading={busy}>
          {busy ? t('verifying') : t('verify')}
        </Button>
      </>
    );
  }

  return (
    <ModalDialog
      dialogStatus={open}
      toggleDialog={onCancel}
      title={title}
      icon={<ShieldOutlinedIcon />}
      maxWidth={phase === 'apps' ? 'md' : 'sm'}
      fullWidth
      disableClickAway
      buttonStack={buttons}
    >
      {phase === 'apps' &&
        <Stack spacing={2}>
          <Typography variant="body2">{t('twoFactorChooseAppIntro')}</Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2
          }}>
            {[
              { key: 'ios', label: t('twoFactorApplePlatform'), icon: <AppleIcon /> },
              { key: 'android', label: t('twoFactorAndroidPlatform'), icon: <AndroidIcon /> }
            ].map(platform => (
              <Paper key={platform.key} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  {platform.icon}
                  <Typography sx={{ fontWeight: 600 }}>{platform.label}</Typography>
                </Stack>
                <Stack spacing={1}>
                  {AUTHENTICATOR_APPS.map(app => (
                    <Link
                      key={app.name}
                      href={app[platform.key]}
                      target="_blank"
                      // noopener/noreferrer: without it the store tab gets a handle on this
                      // window via window.opener, mid-authentication.
                      rel="noopener noreferrer"
                      underline="hover"
                      variant="body2"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {app.name}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('twoFactorAlreadyHaveApp')} {t('twoFactorAppOpensStore')}
          </Typography>
        </Stack>
      }

      {phase === 'loading' &&
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress size={32} />
        </Stack>
      }

      {phase === 'recovery' &&
        <Stack spacing={2}>
          <Typography variant="body2">{t('recoveryCodesIntro')}</Typography>
          <Alert severity="warning">{t('recoveryCodesWarning')}</Alert>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1
            }}>
              {recoveryCodes.map(rc => (
                <Typography key={rc} sx={{ fontFamily: 'monospace', fontSize: '0.95rem', letterSpacing: 1 }}>
                  {rc}
                </Typography>
              ))}
            </Box>
          </Paper>
          <Stack direction="row" spacing={1}>
            <Button variant="text" startIcon={<ContentCopyIcon />} onClick={copyCodes}>
              {t('copyCodes')}
            </Button>
            <Button variant="text" startIcon={<DownloadIcon />} onClick={downloadCodes}>
              {t('downloadCodes')}
            </Button>
          </Stack>
          {notice != null && <Typography variant="caption" color="success.main">{notice}</Typography>}
        </Stack>
      }

      {(phase === 'scan' || phase === 'code') &&
        <Stack spacing={2}>
          <Typography variant="body2">
            {isSetup ? t('twoFactorSetupIntro') : t('twoFactorVerifyIntro')}
          </Typography>

          {isSetup && setupData != null &&
            <>
              {/* The app suggestions live on the previous step now, so they are not repeated. */}
              <Stack alignItems="center" spacing={1}>
                <Box
                  component="img"
                  src={setupData.qr}
                  alt={t('twoFactorScanQr')}
                  sx={{ width: 200, height: 200, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('twoFactorManualKey')}
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: 1, wordBreak: 'break-all', textAlign: 'center' }}>
                  {setupData.manualKey}
                </Typography>
              </Stack>
              <Divider />
            </>
          }

          <MuiTextField
            autoFocus
            fullWidth
            value={code}
            onChange={handleCodeChange}
            onKeyDown={onKeyDown}
            label={usingRecoveryCode ? t('recoveryCode') : t('verificationCode')}
            placeholder={usingRecoveryCode ? '' : '000000'}
            error={error != null}
            helperText={error != null ? error : (usingRecoveryCode ? t('enterRecoveryCode') : t('twoFactorEnterCode'))}
            inputProps={{
              inputMode: usingRecoveryCode ? 'text' : 'numeric',
              // Password managers latch onto autocomplete="one-time-code" and anchor their
              // "enable autofill" callouts under the field, covering the hint text. Nothing here
              // is a stored credential -- the code comes off the user's phone -- so every
              // credential-manager heuristic is opted out of explicitly. The data-* attributes
              // are the documented opt-outs for 1Password, LastPass and Bitwarden respectively;
              // iCloud Passwords has no documented flag and is handled by not declaring an
              // autocomplete token it recognises.
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
              'data-1p-ignore': 'true',
              'data-lpignore': 'true',
              'data-bwignore': 'true',
              'data-form-type': 'other',
              style: usingRecoveryCode
                ? { fontFamily: 'monospace', letterSpacing: 2 }
                : { fontFamily: 'monospace', letterSpacing: 8, fontSize: '1.3rem' }
            }}
          />

          {!isSetup &&
            <Link component="button" type="button" variant="caption" underline="hover"
                  onClick={toggleRecoveryCode} sx={{ alignSelf: 'flex-start' }}>
              {usingRecoveryCode ? t('useAuthenticatorCode') : t('useRecoveryCode')}
            </Link>
          }
        </Stack>
      }
    </ModalDialog>
  );
}

/**
 * The API returns plain-text i18n messages for these endpoints, so surface the server's own
 * wording rather than a generic client-side string.
 */
function messageFrom(err, fallback) {
  const data = err != null && err.response != null ? err.response.data : null;
  if (typeof data === 'string' && data !== '') {
    return data;
  }
  if (data != null && typeof data.message === 'string' && data.message !== '') {
    return data.message;
  }
  return fallback;
}
